import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  type IChartApi,
  type CandlestickData,
  type Time,
} from 'lightweight-charts';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart, PenSquare, TrendingUp, TrendingDown, Activity, FileText, Info } from 'lucide-react';
import { queryKeys } from '@/api/queryKeys';
import { get, post, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Spinner } from '@/components/atoms/Spinner';
import { InvestmentDisclaimer } from '@/components/atoms/InvestmentDisclaimer';
import { AiAnalysisRenderer } from '@/components/organisms/AiAnalysisRenderer';
import { StockQuoteCard } from '@/components/molecules/StockQuoteCard';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { COIN_NAMES_KR, stripKrw } from '@/constants/coins';
import { toast } from '@/store/toastStore';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { cn } from '@/lib/cn';
import { PatternImg, classifyPattern, DirBadge } from '@/components/patterns/patternGraphics';
import { PatternDetailModal } from '@/components/patterns/PatternDetailModal';
import { AdBanner } from '@/components/ads/AdBanner';
import { SEOHead } from '@/components/atoms/SEOHead';
import { JsonLd } from '@/components/atoms/JsonLd';
import { TagBadge, type TagCategory } from '@/components/atoms/TagBadge';

/* ══════════════════════════════════════════════════════════════
 *  타입
 * ══════════════════════════════════════════════════════════════ */

type MarketType = 'KOSPI' | 'KOSDAQ' | 'NASDAQ' | 'NYSE' | 'UPBIT';
type StockStatus = 'normal' | 'halt' | 'warning';

/** KIS 기본정보 API 응답 (GET /tickers/:code/detail `basic`). 원 단위 숫자 또는 null. */
interface TickerBasicApi {
  price: number | null;
  changeAmount: number | null;
  changePct: number | null;
  volume: number | null;
  /** 오늘 시가 — 차트분석 탭 장중 임시 캔들 OHLC 의 시가에 사용 */
  open: number | null;
  high: number | null;
  low: number | null;
  upperLimit: number | null;
  lowerLimit: number | null;
  marketCap: number | null;       // 억원 단위
  foreignOwnRate: number | null;  // %
  per: number | null;
  pbr: number | null;
  eps: number | null;
  faceValue: number | null;
  high52w: number | null;
  low52w: number | null;
  status: StockStatus;
  source: 'kis' | 'naver' | 'none';
  updatedAt: number;
}

/** 프론트 렌더에 쓰는 통합 뷰모델 — API + 종목 메타(이름/시장) 결합 */
interface TickerBasicView {
  name: string;
  code: string;
  market: MarketType;
  status: StockStatus;
  // null 허용 — 필드별 개별 대시(-) 표기
  price: number | null;
  changeAmount: number | null;
  changePct: number | null;
  volume: number | null;           // 세력분석 AI 컨텍스트용
  marketCap: number | null;
  faceValue: number | null;
  high52w: number | null;
  low52w: number | null;
  foreignOwnRate: number | null;
  per: number | null;
  pbr: number | null;
  eps: number | null;
}

/* [2026-04-22] MOCK_ANNUAL / MOCK_QUARTERLY / MOCK_HISTORY 제거.
 *
 *  이전에는 DART 데이터가 빈 배열로 올 때 삼성전자(005930) 기준 mock 으로 대체했는데,
 *  이 때문에 012450 처럼 재무는 있지만 history 가 비어있는 종목에서 "삼성전자공업 창립" 같은
 *  **완전히 다른 종목의 이력이 노출되는 버그** 발생.
 *  빈 배열 그대로 내려보내 FinancialTab 이 자체 "데이터 없음" 빈 상태를 표시한다. */

/* ══════════════════════════════════════════════════════════════
 *  DART Phase 1 응답 타입 (GET /tickers/:code/detail)
 *  공시/뉴스 탭은 제거됐지만 API 응답에는 disclosures 가 계속 포함되므로 타입은 유지.
 * ══════════════════════════════════════════════════════════════ */

interface AnnualRow {
  year: number;
  revenue: number | null;
  operatingProfit: number | null;
  netIncome: number | null;
  eps: number | null;
}

interface QuarterlyRow {
  period: string;
  year: number;
  quarter: number;
  revenue: number | null;
  operatingProfit: number | null;
  netIncome: number | null;
  eps: number | null;
}

interface HistoryItem {
  date: string;
  detail: string;
  /* DART 접수번호 — 있으면 HistoryBlock 에서 원문 링크 버튼 노출 */
  rceptNo?: string | null;
}
interface HistoryGroup {
  capitalReduction: HistoryItem[];
  rightsIssue: HistoryItem[];
  renaming: HistoryItem[];
}

interface DisclosureRow {
  date: string;
  title: string;
  rceptNo: string;
  sourceUrl: string;
}

interface TickerDetail {
  basic: TickerBasicApi | null;
  financials: { annual: AnnualRow[]; quarterly: QuarterlyRow[] };
  history: HistoryGroup;
  disclosures: DisclosureRow[];
}

/* [공시/뉴스 탭 제거됨 — 2026-04-22] 관련 뉴스 mock. 향후 재도입 시 복원.
const MOCK_NEWS = [
  { date: '2026-04-21', title: '삼성전자 1분기 깜짝 실적…HBM 매출 호조', source: '매일경제' },
  { date: '2026-04-20', title: 'HBM 수출 증가, 메모리 반도체 업황 반등세',  source: '한국경제' },
  { date: '2026-04-18', title: 'AI 서버 수요로 D램 가격 반등',                source: '연합뉴스' },
  { date: '2026-04-15', title: '삼성전자, 美 테일러 공장 본격 가동',           source: '이데일리' },
];
*/

/** 세력 분석 종합 — 0~100 점 (높을수록 세력 흔적 가능성 ↑) */
/* ══════════════════════════════════════════════════════════════
 *  세력분석 응답 타입 — GET /tickers/:code/manipulation
 * ══════════════════════════════════════════════════════════════ */

interface ManipDimension {
  score: number;
  label: string;
  [k: string]: unknown;
}

interface ManipOk {
  status: 'ok';
  ticker: string;
  totalScore: number;
  dimensions: {
    volume: ManipDimension & {
      ratio?: number;
      spikes?: number;
      /* 이중 윈도우 (작업 3, 2026-05-07) */
      midRatio?: number | null;
      midScore?: number | null;
      shortScore?: number | null;
      mode?: 'short_only' | 'mid_only' | 'dual';
    };
    volatility: ManipDimension & {
      cv?: number;
      avgRange?: number;
      /* 시장 대비 상대 변동성 (작업 2, 2026-05-07) */
      marketCv?: number | null;
      relCv?: number | null;
      mode?: 'relative' | 'absolute';
    };
    foreign: ManipDimension & {
      change?: number;
      trend?: 'stable' | 'inflow' | 'outflow' | 'unknown';
      current?: number | null;
    };
    disclosure: ManipDimension & { count?: number; byType?: Record<string, number> };
  };
  meta?: {
    ohlcvDays: number;
    oldestOhlcvDate: string | null;
    tradingStatus: 'normal' | 'halt' | 'warning' | 'unknown';
  };
  calculatedAt: string;
}

interface ManipInsufficient {
  status: 'insufficient_data';
  ticker: string;
  reason: string;
}

type ManipResponse = ManipOk | ManipInsufficient;

/* ══════════════════════════════════════════════════════════════
 *  포맷 헬퍼
 * ══════════════════════════════════════════════════════════════ */

function comma(n: number): string {
  return n.toLocaleString('ko-KR');
}

/** 양수 빨강 / 음수 파랑 (한국 증시 컨벤션) */
function trendClass(n: number): string {
  if (n > 0) return 'text-red-500';
  if (n < 0) return 'text-blue-500';
  return 'text-[var(--color-text-sub)]';
}

/** 억원 단위 숫자를 "3,100.0조원" / "258.0조원" 형식으로 변환 */
function toEokToJo(eok: number): string {
  const jo = eok / 10_000;
  if (jo >= 1) return `${jo.toFixed(1)}조원`;
  return `${comma(eok)}억원`;
}

/* ══════════════════════════════════════════════════════════════
 *  기본정보 필드별 null-safe 포맷 유틸
 * ══════════════════════════════════════════════════════════════ */

const DASH = '-';

function fmtPrice(n: number | null): string {
  return n == null ? DASH : `${comma(n)}원`;
}
function fmtDelta(n: number | null): string {
  if (n == null) return DASH;
  return n > 0 ? `+${comma(n)}원` : `${comma(n)}원`;
}
function fmtPct(n: number | null): string {
  if (n == null) return DASH;
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}
function fmtRatio(n: number | null): string {
  return n == null ? DASH : `${n.toFixed(2)}배`;
}
function fmtPercent(n: number | null): string {
  return n == null ? DASH : `${n.toFixed(2)}%`;
}
/** 시가총액 (억원 단위 입력) → "350.2조원" / "1,234억원" */
function fmtMarketCap(eok: number | null): string {
  return eok == null ? DASH : toEokToJo(eok);
}
/** 한국 컨벤션: 상승 빨강 / 하락 파랑 */
function trendClassNullable(n: number | null): string {
  if (n == null) return 'text-[var(--color-text-sub)]';
  return trendClass(n);
}

/* 시장 배지 — 중성 토큰 단일 톤 (토스 스타일: 텍스트만으로 구분, 색상 남발 금지) */
const MARKET_BADGE: Record<MarketType, { label: string; cls: string }> = {
  KOSPI:  { label: 'KOSPI',  cls: 'badge badge-muted' },
  KOSDAQ: { label: 'KOSDAQ', cls: 'badge badge-muted' },
  NASDAQ: { label: 'NASDAQ', cls: 'badge badge-muted' },
  NYSE:   { label: 'NYSE',   cls: 'badge badge-muted' },
  UPBIT:  { label: 'UPBIT',  cls: 'badge badge-muted' },
};

/* 종목 상태 배지 — 의미에 따라 톤 차별.
   normal 은 중성 (토스 스타일의 "기본값은 조용하게"),
   warning/halt 은 warn/rise 토큰으로 명확히 위험 신호 전달. */
const STATUS_BADGE: Record<StockStatus, { label: string; cls: string }> = {
  normal:  { label: '정상',    cls: 'badge badge-muted' },
  warning: { label: '관리종목', cls: 'badge badge-warn' },
  halt:    { label: '거래정지', cls: 'badge badge-rise' },
};

/** 세력 점수 → 레벨/색상 매핑 (0~29 green · 30~49 yellow · 50~69 orange · 70~ red).
    4단계로 세분화 — AdSense 심사 대비 과도한 단정 표현 회피. */
function manipLevel(score: number): { label: string; color: 'low' | 'info' | 'mid' | 'high'; cls: string; bar: string } {
  if (score >= 70) {
    return { label: '이상 징후 강함', color: 'high', cls: 'text-red-600', bar: 'bg-red-500' };
  }
  if (score >= 50) {
    return { label: '주의 관찰', color: 'mid', cls: 'text-orange-500', bar: 'bg-orange-500' };
  }
  if (score >= 30) {
    return { label: '약한 신호', color: 'info', cls: 'text-yellow-600', bar: 'bg-yellow-500' };
  }
  return { label: '정상 범위', color: 'low', cls: 'text-emerald-600', bar: 'bg-emerald-500' };
}

/* ══════════════════════════════════════════════════════════════
 *  페이지 본체
 * ══════════════════════════════════════════════════════════════ */

/* 2026-04-22 탭 구조 개편 — 4개 → 2개
   - analysis: AI 분석 + 기본정보 + 재무분석 을 한 탭에서 스크롤로 순차 노출
   - manipulation: 세력분석 — 2026-05-26 전체 유저 claude -p CLI 버전(ManipulationCliTab)으로 전환.
       (SDK 프론트 컴포넌트는 제거. 백엔드 SDK /manipulation-ai 엔드포인트는 유지)
   공시/뉴스 탭은 완전 제거 (Phase 2 이후 재도입 검토). */
type TabKey = 'analysis' | 'manipulation' | 'chart';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'analysis',     label: '종목분석' },
  { key: 'manipulation', label: '세력분석' },
  { key: 'chart',        label: '차트분석' }, // DEV — 차트 패턴 분석(claude -p)
];

export function TickerPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuthStore();
  const { setLoginModalOpen } = useUIStore();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabKey>('analysis');

  /* 종목 변경 시 항상 종목분석 탭으로 초기화 — 세력분석 선택 상태가 새 종목에 끌려가는 버그 방지 */
  useEffect(() => {
    setTab('analysis');
  }, [code]);

  /* 실 시세는 현재 stub 이라 null — mock 헤더와 섞어 표시. 실 연동 시 교체. */
  const { data: priceData } = useQuery({
    queryKey: queryKeys.tickers.detail(code!),
    queryFn: () =>
      get<{ ticker: string; price: number; changePct: number; name?: string; asset_type?: string } | null>(
        `/market/prices/${code}`,
      ),
    enabled: !!code,
    staleTime: 60_000,
  });

  /* 종목 상세 (기본정보 + 재무 + 이력 + 공시).
     ※ queryKey 를 `full-detail` 로 구분해야 한다.
     `queryKeys.tickers.detail(code)` 는 priceData 용으로 동일한 `['tickers','detail',code]`
     를 반환하기 때문에, 같은 key 를 쓰면 React Query 가 priceData stub(null)을 재사용해
     detailData 가 null 로 고정되는 버그가 발생한다. */
  const {
    data: detailData,
    isLoading: detailLoading,
    isError: detailError,
    error: detailErrorObj,
  } = useQuery({
    queryKey: ['tickers', 'full-detail', code],
    queryFn: () => get<TickerDetail>(`/tickers/${code}/detail`),
    enabled: !!code,
    staleTime: 60 * 60 * 1000,   // 1시간 — DART 캐시 주기와 맞춤
    // 404 는 재시도 의미 없음
    retry: (count, err) => {
      const status = (err as any)?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });

  /* 가이던스 정합성 분석 글 존재 여부 — 있으면 헤더에 "분석 보기" 링크 노출.
     별도 가벼운 쿼리(LIMIT 1 + 인덱스). 404 처리 불필요(빈 결과는 exists=false). */
  const { data: guidanceLink } = useQuery({
    queryKey: queryKeys.tickers.guidance(code ?? ''),
    queryFn: () =>
      get<{ exists: boolean; post_id?: number; title?: string }>(
        `/tickers/${code}/guidance`,
      ),
    enabled: !!code,
    staleTime: 10 * 60 * 1000, // 10분
    retry: 1,
  });

  /* 존재하지 않는 종목 — 404 시 NotFoundPage 렌더링 (아래 분기).
     500/503 등 일시적 오류는 페이지 내 에러 UI 로 유지. */
  const isTickerNotFound = (detailErrorObj as any)?.response?.status === 404;

  /* 국내 상장주(6자리 숫자) 만 DART 재무/공시 지원.
     해외/코인/지수 등은 Financial/Disclosures 탭에 "지원하지 않음" 안내 표시. */
  const isKoreanStock = /^\d{6}$/.test(code ?? '');

  /* API 응답을 그대로 사용 — 빈 배열이면 FinancialTab 이 "데이터 없음" 빈 상태를 표시.
     종목 간 데이터 혼선 방지를 위해 mock fallback 제거 (012450 ↔ 005930 교차 노출 버그). */
  const annual = detailData?.financials.annual ?? [];
  const quarterly = detailData?.financials.quarterly ?? [];
  const history: HistoryGroup = detailData?.history ?? {
    capitalReduction: [],
    rightsIssue: [],
    renaming: [],
  };

  const { data: subs } = useQuery({
    queryKey: ['subscriptions', 'me'],
    queryFn: () => get<{ list: Array<{ ticker: string }> }>('/subscriptions'),
    enabled: isLoggedIn,
  });
  const isSubscribed = subs?.list?.some((s) => s.ticker === code);

  const subscribeMutation = useMutation({
    mutationFn: () =>
      post('/subscriptions', {
        ticker: code,
        assetType: priceData?.asset_type ?? 'stock',
        threshold: 5,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'me'] });
      toast.success('관심종목에 추가됐어요.');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '추가에 실패했어요.'),
  });
  const unsubscribeMutation = useMutation({
    mutationFn: () => del(`/subscriptions/${code}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions', 'me'] });
      toast('관심종목에서 제거됐어요.');
    },
  });

  /* 종목 메타 (이름/시장/assetType) — GET /tickers/:code */
  const { data: meta } = useQuery({
    queryKey: ['tickers', 'meta', code],
    queryFn: () =>
      get<{
        code: string;
        name: string;
        nameEn: string | null;
        assetType: 'stock' | 'coin' | 'us_stock';
        market: string;
      }>(`/tickers/${code}`),
    enabled: !!code,
    staleTime: 24 * 60 * 60 * 1000,
  });

  /* 종목 표시명 — 코인은 로컬 매핑 우선, 주식은 메타 API name */
  const displayName = useMemo(() => {
    const tickerCode = code ?? '';
    const isCoin = tickerCode.startsWith('KRW-');
    return (
      (isCoin ? COIN_NAMES_KR[tickerCode] : null) ??
      meta?.name ??
      priceData?.name ??
      (isCoin ? stripKrw(tickerCode) : tickerCode)
    );
  }, [code, meta?.name, priceData?.name]);

  /* API basic + 메타 결합한 뷰모델. basic 이 null 이면 모든 숫자 필드 null 로 표시. */
  const basicApi = detailData?.basic ?? null;

  const marketKey: MarketType =
    (['KOSPI', 'KOSDAQ', 'NASDAQ', 'NYSE', 'UPBIT'] as const).find(
      (m) => m === meta?.market,
    ) ?? 'KOSPI';

  const basic: TickerBasicView = {
    name: displayName,
    code: code ?? '',
    market: marketKey,
    status: basicApi?.status ?? 'normal',
    price: basicApi?.price ?? null,
    changeAmount: basicApi?.changeAmount ?? null,
    changePct: basicApi?.changePct ?? null,
    volume: basicApi?.volume ?? null,
    marketCap: basicApi?.marketCap ?? null,
    faceValue: basicApi?.faceValue ?? null,
    high52w: basicApi?.high52w ?? null,
    low52w: basicApi?.low52w ?? null,
    foreignOwnRate: basicApi?.foreignOwnRate ?? null,
    per: basicApi?.per ?? null,
    pbr: basicApi?.pbr ?? null,
    eps: basicApi?.eps ?? null,
  };

  const marketBadge = MARKET_BADGE[basic.market];
  const statusBadge = STATUS_BADGE[basic.status];

  const handleSubscribeToggle = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    if (isSubscribed) unsubscribeMutation.mutate();
    else subscribeMutation.mutate();
  };

  const goWriteDiary = () => {
    if (!isLoggedIn) return setLoginModalOpen(true);
    navigate(`/diary/new?ticker=${encodeURIComponent(code ?? '')}&type=buy`);
  };

  if (!code) {
    return <Skeleton className="m-5 h-64" />;
  }

  // FinancialProduct JSON-LD — 종목 마스터 정보
  const financialProductSchema = {
    '@type': 'FinancialProduct',
    name: displayName,
    tickerSymbol: code ?? '',
    description: `${displayName}(${code}) AI 종목 분석, 세력분석, 공시 분석 정보, 재무지표 (${basic.market})`,
    url: `https://community.insidemation.com/ticker/${code}`,
    category: marketKey === 'UPBIT' ? 'Cryptocurrency' : 'Stock',
    provider: { '@type': 'Organization', name: '시세공간' },
  };

  if (isTickerNotFound) {
    return <NotFoundPage />;
  }

  return (
    <div className="container-base py-6">
      <SEOHead
        title={`${displayName}(${code})`}
        description={`${displayName} AI 분석, 세력분석, 공시 분석 정보를 확인하세요`}
        canonical={`/ticker/${code}`}
      />
      <JsonLd data={financialProductSchema} />
      {/* ── 상단 헤더 ─────────────────────────── */}
      <section className="mb-5 rounded-2xl border border-[var(--color-border)] bg-white p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-extrabold md:text-3xl">{basic.name}</h1>
              <span className="rounded-md bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-600">
                {basic.code}
              </span>
              <span className={marketBadge.cls}>
                {marketBadge.label}
              </span>
              <span className={statusBadge.cls}>
                {statusBadge.label}
              </span>
            </div>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="text-3xl font-extrabold tabular-nums">{fmtPrice(basic.price)}</span>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xs text-[var(--color-text-sub)]">전일 대비</span>
                <span className={cn('text-sm font-semibold tabular-nums', trendClassNullable(basic.changeAmount))}>
                  {fmtDelta(basic.changeAmount)} ({fmtPct(basic.changePct)})
                </span>
              </div>
            </div>
            {/* 시초가 / 종가(전일) — 국내 6자리 상장주 한정. StockQuoteCard 와 같은 queryKey 사용해
                실제 API 호출은 한 번만 일어남 (React Query dedupe). */}
            {code && /^\d{6}$/.test(code) && <TickerHeaderOpenClose code={code} />}

            {/* 가이던스 정합성 분석 게시글이 있을 때만 노출. 클릭 시 해당 글로 이동. */}
            {guidanceLink?.exists && guidanceLink.post_id && (
              <Link
                to={`/post/${guidanceLink.post_id}`}
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-primary)] bg-blue-50 px-3 py-1.5 text-sm font-semibold text-[var(--color-primary)] transition-colors hover:bg-blue-100"
                title={guidanceLink.title}
              >
                <FileText className="h-4 w-4" />
                가이던스 분석 보기
                <span aria-hidden>→</span>
              </Link>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant={isSubscribed ? 'secondary' : 'primary'}
              onClick={handleSubscribeToggle}
              loading={subscribeMutation.isPending || unsubscribeMutation.isPending}
            >
              <Heart className={cn('h-4 w-4', isSubscribed && 'fill-current')} />
              {isSubscribed ? '관심종목' : '관심종목 추가'}
            </Button>
            <Button variant="secondary" onClick={goWriteDiary}>
              <PenSquare className="h-4 w-4" />
              매매일지
            </Button>
          </div>
        </div>

        {/* ── 태그 뱃지 (Phase 7) — risk → size → theme(상위 confidence) 우선 최대 3개 */}
        <TickerTopTags code={code!} />
      </section>

      {/* ── 탭 ─────────────────────────── */}
      <nav className="mb-5">
        <div className="flex w-full gap-1 overflow-x-auto rounded-md bg-[var(--color-tab-bg)] p-1">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  'shrink-0 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-all duration-150',
                  active
                    ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                    : 'bg-transparent text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)] hover:text-[var(--color-text)]',
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── 탭별 콘텐츠 ─────────────────────────── */}
      {tab === 'analysis' && (
        <AnalysisTab
          code={code}
          basic={basic}
          annual={annual}
          quarterly={quarterly}
          history={history}
          isLoading={detailLoading}
          isError={detailError}
          notSupported={!isKoreanStock}
        />
      )}
      {/* 세력분석 탭 — 2026-05-26 전체 유저 claude -p CLI 버전으로 전환 */}
      {tab === 'manipulation' && (
        <ManipulationCliTab
          code={code}
          basic={basic}
          isActive={tab === 'manipulation'}
        />
      )}
      {/* 차트분석 탭 (DEV) — 캔들차트 + claude -p 패턴 분석 */}
      {tab === 'chart' && <ChartAnalysisTab code={code} isActive={tab === 'chart'} />}

      {/* 광고: 페이지 하단 디스플레이 — multiplex-v 전역 비활성화로 display-rect 로 교체 (2026-05-29). 모든 탭 공통 노출 */}
      <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />

      {/* ── 투자 위험 고지 (페이지 하단 공통) ─────────────── */}
      <InvestmentDisclaimer variant="full" className="mt-8" />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
 *  TAB — 종목분석 (AI 분석 + 기본정보 + 재무분석 한 탭에 수직 나열)
 * ══════════════════════════════════════════════════════════════ */

function AnalysisTab({
  code,
  basic,
  annual,
  quarterly,
  history,
  isLoading,
  isError,
  notSupported,
}: {
  code?: string;
  basic: TickerBasicView;
  annual: AnnualRow[];
  quarterly: QuarterlyRow[];
  history: HistoryGroup;
  isLoading: boolean;
  isError: boolean;
  notSupported: boolean;
}) {
  const isKoreanStock = !!code && /^\d{6}$/.test(code);

  return (
    <div className="space-y-6">
      {/* ⓪ 시세 & 투자자 동향 — 국내 6자리 상장주만 (KIS API). AI 분석 위에 배치. */}
      {isKoreanStock && code && <StockQuoteCard ticker={code} />}

      {/* ① AI 기본 분석 — 페이지 진입 시 자동 호출 (detailLoading 끝난 뒤) */}
      <AiAnalysisSection
        code={code}
        basic={basic}
        annual={annual}
        detailLoading={isLoading}
      />

      {/* ② 기본정보 */}
      <section>
        <h2 className="mb-3 section-title">기본정보</h2>
        <BasicTab basic={basic} notSupported={notSupported} />
      </section>

      {/* ③ 재무분석 */}
      <section>
        <h2 className="mb-3 section-title">재무분석</h2>
        <FinancialTab
          annual={annual}
          quarterly={quarterly}
          history={history}
          isLoading={isLoading}
          isError={isError}
          notSupported={notSupported}
        />
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
 *  AI 기본 분석 섹션 — Claude Sonnet 4
 *  2026-04-22 개편: 페이지 진입 시 자동 호출 (useQuery + POST).
 *  백엔드에서 DB(ticker_ai_analysis) 우선 조회 → 30일 경과 또는 새 공시 발생 시에만 Claude 재호출.
 *  따라서 자동 호출이어도 반복 Claude API 비용은 발생하지 않음.
 * ══════════════════════════════════════════════════════════════ */

interface AiAnalysisResponse {
  analysis: string;
  analyzedAt: string;         // ISO timestamp
  dartRceptNo: string | null;
  reanalyzed: boolean;
}

interface AiPayload {
  basic: {
    name: string;
    code: string;
    /* 현재가/등락은 프롬프트에서 제외 — PER/PBR/EPS/시총/외국인 중심 분석 */
    per?: number | null;
    pbr?: number | null;
    eps?: number | null;
    marketCap?: string | null;
    foreignOwnRate?: number | null;
    /* 배당수익률 — KIS/DART 에서 안정적으로 제공되지 않아 현재는 항상 null.
       향후 배당 API 연동 시 값 전달 예정. */
    dividendYield?: number | null;
  };
  /* 최근 2년 연간 재무 — 백엔드에서 매출 성장률/영업이익률 계산 */
  annual?: AnnualRow[] | null;
}

function AiAnalysisSection({
  code,
  basic,
  annual,
  detailLoading,
}: {
  code?: string;
  basic: TickerBasicView;
  annual: AnnualRow[];
  detailLoading: boolean;
}) {
  /* 자동 호출 — detail 로드 이후 한 번만. staleTime 30분으로 중복 호출 억제. */
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ticker', 'ai', code],
    queryFn: () => {
      const payload: AiPayload = {
        basic: {
          name: basic.name,
          code: basic.code,
          per: basic.per,
          pbr: basic.pbr,
          eps: basic.eps,
          // 시총을 "350.2조원" 포맷 문자열로 전달 (AI 가 그대로 인용 가능)
          marketCap: basic.marketCap != null ? fmtMarketCap(basic.marketCap) : null,
          foreignOwnRate: basic.foreignOwnRate,
          dividendYield: null,   // 배당수익률 데이터 소스 연결 전까지 null
        },
        /* 최근 2년 전달 — 전년 대비 매출 성장률 계산용 */
        annual: annual.slice(0, 2),
      };
      /* [DEV] 종목분석 claude -p CLI 버전 호출 — 운영 프론트는 /ai-analysis(SDK) 유지.
         CLI 첫 생성은 더 걸릴 수 있어 백엔드 타임아웃(120s)에 맞춰 120s 로 확장. */
      return post<AiAnalysisResponse>(`/tickers/${code}/ai-analysis-cli`, payload, { timeout: 120_000 });
    },
    enabled: !!code && !detailLoading,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
      <h2 className="mb-3 section-title">AI 기본 분석</h2>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-[color-mix(in_srgb,var(--color-primary)_20%,transparent)] bg-[color-mix(in_srgb,var(--color-primary)_6%,transparent)] p-4 text-sm text-[var(--color-text-sub)]">
          <Spinner size="sm" />
          AI가 분석 중이에요. 분석하는데 다소 시간이 걸릴 수 있어요.
        </div>
      )}

      {!isLoading && isError && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-sm text-[var(--color-text-sub)]">
          AI 분석을 불러올 수 없어요.
        </div>
      )}

      {!isLoading && !isError && data && (
        <AiAnalysisRenderer analysis={data.analysis} />
      )}

      <p className="mt-3 text-xs text-[var(--color-text-sub)]">
        본 분석은 투자 권유가 아닌 참고용 정보이며, 투자 결과의 책임은 투자자 본인에게 있습니다.
      </p>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════
 *  기본정보 (AnalysisTab 내부 섹션으로 사용)
 * ══════════════════════════════════════════════════════════════ */

function BasicTab({
  basic,
  notSupported,
}: {
  basic: TickerBasicView;
  /** 해외/코인 등 KIS 지원 범위를 벗어난 종목 */
  notSupported?: boolean;
}) {
  if (notSupported) {
    return (
      <section className="rounded-2xl border border-dashed border-[var(--color-border)] bg-white p-10 text-center text-sm text-[var(--color-text-sub)]">
        기본 정보를 지원하지 않아요.
      </section>
    );
  }

  const changeCls = trendClassNullable(basic.changeAmount);

  const rows: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: '현재가',
      value: <span className="font-semibold tabular-nums">{fmtPrice(basic.price)}</span>,
    },
    {
      label: '전일대비',
      value: (
        <span className={cn('font-semibold tabular-nums', changeCls)}>
          {fmtDelta(basic.changeAmount)} ({fmtPct(basic.changePct)})
        </span>
      ),
    },
    { label: '시가총액',       value: <span className="font-semibold">{fmtMarketCap(basic.marketCap)}</span> },
    { label: '액면가',         value: <span className="font-semibold tabular-nums">{fmtPrice(basic.faceValue)}</span> },
    { label: '52주 최고',      value: <span className="font-semibold tabular-nums">{fmtPrice(basic.high52w)}</span> },
    { label: '52주 최저',      value: <span className="font-semibold tabular-nums">{fmtPrice(basic.low52w)}</span> },
    { label: '외국인 보유율',  value: <span className="font-semibold tabular-nums">{fmtPercent(basic.foreignOwnRate)}</span> },
    { label: 'PER',            value: <span className="font-semibold tabular-nums">{fmtRatio(basic.per)}</span> },
    { label: 'PBR',            value: <span className="font-semibold tabular-nums">{fmtRatio(basic.pbr)}</span> },
    { label: 'EPS',            value: <span className="font-semibold tabular-nums">{fmtPrice(basic.eps)}</span> },
  ];

  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
      <h2 className="mb-4 section-title">기본 정보</h2>
      {/* 2분할 레이아웃 — 각 행에 구분선 + 좌(라벨)/우(값) 양 끝 정렬 + 충분한 간격.
         gray-100 은 흰 배경에서 대비가 너무 약해 프로젝트 표준 --color-border 사용. */}
      <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex items-center justify-between gap-4 border-b border-[var(--color-border)] py-3"
          >
            <span className="text-sm text-gray-500">{r.label}</span>
            <span className="text-right font-medium tabular-nums">{r.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════
 *  TAB 2 — 재무분석
 * ══════════════════════════════════════════════════════════════ */

/** 숫자 or null → "3,100.0조원" / "-" */
function toEokOrDash(n: number | null): string {
  return n == null ? '-' : toEokToJo(n);
}
function epsOrDash(n: number | null): string {
  return n == null ? '-' : `${comma(n)}원`;
}

function FinancialTab({
  annual,
  quarterly,
  history,
  isLoading,
  isError,
  notSupported,
}: {
  annual: AnnualRow[];
  quarterly: QuarterlyRow[];
  history: HistoryGroup;
  isLoading: boolean;
  isError: boolean;
  notSupported: boolean;
}) {
  if (notSupported) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-white p-10 text-center text-sm text-[var(--color-text-sub)]">
        해외 종목은 재무 데이터를 지원하지 않아요.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="space-y-5">
        <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
          <div className="mb-4 h-6 w-32 animate-pulse rounded bg-gray-100" />
          <div className="space-y-2">
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
          </div>
        </section>
        <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
          <div className="mb-4 h-6 w-32 animate-pulse rounded bg-gray-100" />
          <div className="space-y-2">
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
            <div className="h-6 w-full animate-pulse rounded bg-gray-100" />
          </div>
        </section>
        <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
          <div className="mb-4 h-6 w-20 animate-pulse rounded bg-gray-100" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="h-24 animate-pulse rounded bg-gray-100" />
            <div className="h-24 animate-pulse rounded bg-gray-100" />
            <div className="h-24 animate-pulse rounded bg-gray-100" />
          </div>
        </section>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-white p-10 text-center text-sm text-[var(--color-text-sub)]">
        데이터를 불러올 수 없어요.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* 연간 실적 */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
        <h2 className="mb-4 section-title">연간 실적 (최근 3년)</h2>
        {annual.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--color-text-sub)]">
            연간 재무 데이터가 없어요.
          </p>
        ) : (
        <div className="-mx-2 overflow-x-auto px-2">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-[var(--color-text-sub)]">
                <th className="px-2 py-2 text-left font-medium">연도</th>
                <th className="px-2 py-2 text-right font-medium">매출액</th>
                <th className="px-2 py-2 text-right font-medium">영업이익</th>
                <th className="px-2 py-2 text-right font-medium">당기순이익</th>
                <th className="px-2 py-2 text-right font-medium">EPS</th>
              </tr>
            </thead>
            <tbody>
              {annual.map((row) => (
                <tr key={row.year} className="border-b border-[var(--color-border)] last:border-0">
                  <td className="px-2 py-2.5 font-semibold">{row.year}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{toEokOrDash(row.revenue)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{toEokOrDash(row.operatingProfit)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{toEokOrDash(row.netIncome)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{epsOrDash(row.eps)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>

      {/* 분기 실적 */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
        <h2 className="mb-4 section-title">분기 실적 (최근 4분기)</h2>
        {quarterly.length === 0 ? (
          <p className="py-8 text-center text-sm text-[var(--color-text-sub)]">
            분기 재무 데이터가 없어요.
          </p>
        ) : (
        <div className="-mx-2 overflow-x-auto px-2">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-[var(--color-text-sub)]">
                <th className="px-2 py-2 text-left font-medium">기간</th>
                <th className="px-2 py-2 text-right font-medium">매출액</th>
                <th className="px-2 py-2 text-right font-medium">영업이익</th>
                <th className="px-2 py-2 text-right font-medium">당기순이익</th>
                <th className="px-2 py-2 text-right font-medium">EPS</th>
              </tr>
            </thead>
            <tbody>
              {quarterly.map((row) => (
                <tr key={row.period} className="border-b border-[var(--color-border)] last:border-0">
                  <td className="px-2 py-2.5 font-semibold">{row.period}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{toEokOrDash(row.revenue)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{toEokOrDash(row.operatingProfit)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{toEokOrDash(row.netIncome)}</td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{epsOrDash(row.eps)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>

      {/* 이력 타임라인 */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
        <h2 className="mb-4 section-title">이력</h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <HistoryBlock title="감자 이력"       items={history.capitalReduction} />
          <HistoryBlock title="유상증자 이력"   items={history.rightsIssue} />
          <HistoryBlock title="사명 변경 이력"  items={history.renaming} />
        </div>
      </section>
    </div>
  );
}

function HistoryBlock({
  title,
  items,
}: {
  title: string;
  items: HistoryItem[];
}) {
  /* DART 원문 파싱으로 description 이 이미 상세 내역("코센 → 이렘", "10:1 무상감자" 등) 을
     담고 있으므로 원문 링크 버튼은 제거. 날짜 + 상세 내역만 표시. */
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--color-text-sub)]">해당 이력 없음</p>
      ) : (
        <ul className="space-y-3 border-l-2 border-[var(--color-border)] pl-3">
          {items.map((it, i) => (
            <li key={i}>
              <div className="text-xs font-semibold text-[var(--color-text-sub)]">{it.date}</div>
              <div className="mt-0.5 text-sm">{it.detail}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* [공시/뉴스 탭 제거됨 — 2026-04-22]
 *  기존 DisclosuresTab 컴포넌트는 종목분석/세력분석 2-탭 구조 개편으로 제거.
 *  Phase 2 에서 재도입 시 git history 참고.
 */

/* ══════════════════════════════════════════════════════════════
 *  세력분석 탭 (claude -p CLI 버전) — ManipulationTab 의 복사본.
 *  UI/항목/4차원 점수(GET /manipulation) 모두 동일하고,
 *  AI 의견만 AiManipCliSection(GET /manipulation-ai-cli) 으로 교체.
 * ══════════════════════════════════════════════════════════════ */

function ManipulationCliTab({
  code,
  basic,
  isActive,
}: {
  code?: string;
  basic: TickerBasicView;
  isActive: boolean;
}) {
  /* 국내 6자리 종목만 지원. 코인/해외주식은 분석 대상 아님. */
  const isDomestic = !!code && /^\d{6}$/.test(code);

  /* 4차원 점수는 SDK 버전과 동일 엔드포인트/데이터 — queryKey 공유로 React Query dedupe */
  const manipQuery = useQuery<ManipResponse>({
    queryKey: ['ticker', code, 'manipulation'],
    queryFn: () => get<ManipResponse>(`/tickers/${code}/manipulation`),
    enabled: isActive && isDomestic,
    staleTime: 10 * 60_000, // 서버 Redis TTL 과 동일
    retry: false,
  });

  const data = manipQuery.data;
  const loading = manipQuery.isLoading;
  const apiError = manipQuery.isError;

  return (
    <div className="space-y-5">
      {/* AI 의견 — claude -p CLI 엔드포인트 사용 */}
      <AiManipCliSection code={code} basic={basic} enabled={isActive && !!code} />

      {/* 국내 상장주 외 안내 */}
      {!isDomestic && (
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <p className="text-sm text-[var(--color-text-sub)]">
            세력분석은 국내 상장(KOSPI/KOSDAQ) 종목만 지원해요.
          </p>
        </section>
      )}

      {/* 로딩 */}
      {isDomestic && loading && (
        <>
          <Skeleton className="h-40 w-full" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </>
      )}

      {/* API 오류 */}
      {isDomestic && apiError && (
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <p className="text-sm text-[var(--color-text-sub)]">
            분석 데이터를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
          </p>
        </section>
      )}

      {/* 데이터 자체가 0건인 경우만 — 1~19일은 부분 분석으로 표시됨 */}
      {isDomestic && data?.status === 'insufficient_data' && (
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <h2 className="section-title mb-2">분석 데이터 없음</h2>
          <p className="text-sm text-[var(--color-text-sub)]">
            아직 OHLCV 데이터가 수집되지 않은 종목이에요. ({data.reason})
          </p>
        </section>
      )}

      {/* 정상 분석 결과 */}
      {isDomestic && data?.status === 'ok' && (
        <>
          {/* 거래정지 / 신규 상장 안내 — 메타 정보 기반 */}
          <ManipMetaBanner meta={data.meta} />
          <ScoreGauge total={data.totalScore} />
          <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <DimensionCard
              title="거래량 이상도"
              score={data.dimensions.volume.score}
              label={data.dimensions.volume.label}
              sub={formatVolumeSub(data.dimensions.volume)}
              Icon={Activity}
            />
            <DimensionCard
              title="가격 변동성"
              score={data.dimensions.volatility.score}
              label={data.dimensions.volatility.label}
              sub={formatVolatilitySub(data.dimensions.volatility)}
              Icon={TrendingUp}
            />
            <DimensionCard
              title="외국인 수급"
              score={data.dimensions.foreign.score}
              label={data.dimensions.foreign.label}
              sub={formatForeignSub(data.dimensions.foreign)}
              Icon={TrendingDown}
            />
            <DimensionCard
              title="자본거래 공시"
              score={data.dimensions.disclosure.score}
              label={data.dimensions.disclosure.label}
              sub={formatDisclosureSub(data.dimensions.disclosure)}
              Icon={FileText}
              tooltip={
                '감자(1년), 유상증자(1년), 합병(1년),\n' +
                '전환사채·신주인수권부사채(2년),\n' +
                '기타(6개월) 이내 공시만 반영됩니다.\n' +
                '오래된 이력이나 종목코드 변경 이전 공시는\n' +
                '포함되지 않을 수 있어요.'
              }
              footer={
                '최근 2년 이내 자본거래 공시를 반영합니다. ' +
                '오래된 이력은 점수에 포함되지 않을 수 있어요.'
              }
            />
          </section>
          <p className="text-right text-[10px] text-[var(--color-text-sub)]">
            분석 기준 시각 · {new Date(data.calculatedAt).toLocaleString('ko-KR')}
          </p>
        </>
      )}

      {/* 면책조항은 페이지 하단(TickerPage)에서 공통 렌더 */}
    </div>
  );
}

/* ── 세력분석 UI 보조 컴포넌트 ─────────────────────────────── */

/**
 * 메타 정보 배너 — 거래정지 경고 / 신규 상장 안내.
 * KIS API status='halt' 면 빨간 경고. ohlcvDays<20 이면 노란 안내.
 * 둘 다 정상이면 null 반환 (배너 미노출).
 */
function ManipMetaBanner({ meta }: { meta?: ManipOk['meta'] }) {
  if (!meta) return null;
  const isHalt = meta.tradingStatus === 'halt';
  const isWarning = meta.tradingStatus === 'warning';
  const isFresh = meta.ohlcvDays > 0 && meta.ohlcvDays < 20;
  if (!isHalt && !isWarning && !isFresh) return null;

  return (
    <section className="space-y-2">
      {isHalt && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm">
          <span className="text-base">⚠️</span>
          <div>
            <div className="font-bold text-red-700">거래정지 종목</div>
            <p className="mt-0.5 text-xs text-red-600">
              현재 거래가 정지되어 있어요. 아래 분석은 정지 전 마지막 데이터 기준입니다.
            </p>
          </div>
        </div>
      )}
      {isWarning && !isHalt && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
          <span className="text-base">⚠️</span>
          <div>
            <div className="font-bold text-amber-700">투자 주의 종목</div>
            <p className="mt-0.5 text-xs text-amber-700">
              KRX 또는 거래소 차원에서 주의 지정된 종목이에요. 신중한 판단이 필요합니다.
            </p>
          </div>
        </div>
      )}
      {isFresh && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-sm">
          <span className="text-base">📊</span>
          <div>
            <div className="font-bold text-blue-700">관찰 {meta.ohlcvDays}일째</div>
            <p className="mt-0.5 text-xs text-blue-700">
              데이터 누적 중입니다. 거래량·변동성 차원은 20 영업일부터 기본 분석, 80 영업일부터 정밀 분석돼요.
              {meta.oldestOhlcvDate && ` (관찰 시작: ${meta.oldestOhlcvDate})`}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function ScoreGauge({ total }: { total: number }) {
  const level = manipLevel(total);
  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
      <div className="flex items-center justify-between">
        <h2 className="section-title">세력 흔적 가능성</h2>
        <span className={cn('text-sm font-bold', level.cls)}>{level.label}</span>
      </div>
      <div className="mt-4">
        <div className="flex items-baseline justify-between">
          <span className={cn('text-4xl font-extrabold tabular-nums', level.cls)}>
            {total}
          </span>
          <span className="text-xs text-[var(--color-text-sub)]">/ 100</span>
        </div>
        <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full transition-all', level.bar)}
            style={{ width: `${Math.min(100, Math.max(0, total))}%` }}
          />
        </div>
        <div className="mt-2 flex justify-between text-[10px] font-medium text-[var(--color-text-sub)]">
          <span>정상</span>
          <span>약신호</span>
          <span>주의</span>
          <span>강신호</span>
        </div>
      </div>
    </section>
  );
}

function DimensionCard({
  title,
  score,
  label,
  sub,
  Icon,
  tooltip,
  footer,
}: {
  title: string;
  score: number;
  label: string;
  sub: string;
  Icon: React.ComponentType<{ className?: string }>;
  /** 카드 제목 옆 ⓘ 아이콘 호버 시 표시할 툴팁 텍스트 (옵션) */
  tooltip?: string;
  /** 카드 하단에 추가 표시할 작은 안내 문구 (옵션) */
  footer?: string;
}) {
  const l = manipLevel(score);
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className="h-4 w-4 text-[var(--color-text-sub)]" />
          <h3 className="text-sm font-bold">{title}</h3>
          {tooltip && <InfoTooltip text={tooltip} />}
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className={cn('text-2xl font-extrabold tabular-nums', l.cls)}>{score}</span>
        <span className="text-xs text-[var(--color-text-sub)]">/ 100</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={cn('h-full rounded-full', l.bar)}
          style={{ width: `${Math.min(100, Math.max(0, score))}%` }}
        />
      </div>
      <p className={cn('mt-2 text-xs font-semibold', l.cls)}>{label}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-[var(--color-text-sub)]">{sub}</p>
      {footer && (
        <p className="mt-2 text-xs leading-relaxed text-gray-400">{footer}</p>
      )}
    </div>
  );
}

/* 호버/클릭 토글 툴팁 — 차원 카드 제목 옆 ⓘ 아이콘 */
function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-[var(--color-text-sub)] hover:text-[var(--color-primary)]"
        aria-label="추가 설명"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute left-1/2 top-full z-30 mt-1 w-60 -translate-x-1/2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-2.5 text-[11px] leading-relaxed text-[var(--color-text)] shadow-lg whitespace-pre-line"
        >
          {text}
        </span>
      )}
    </span>
  );
}

/* ── 차원별 부가 설명 포맷 ─────────────────────────────── */

function formatVolumeSub(d: ManipOk['dimensions']['volume']): string {
  if (d.label === '데이터 부족') return '거래량 데이터 수집 중 (최소 25일 필요)';
  const sRatio = typeof d.ratio === 'number' ? `${d.ratio.toFixed(2)}배` : '?';
  const mRatio =
    typeof d.midRatio === 'number' && d.midRatio !== null ? `${d.midRatio.toFixed(2)}배` : null;
  const sp = typeof d.spikes === 'number' && d.spikes > 0 ? `급증 ${d.spikes}일` : '';
  /* mode 별 표기 — 사용자 친화적 "1주 vs 4주", "4주 vs 12주" 자연어
     (실제 로직: 단기 5일/20일, 중기 20일/60일) */
  if (d.mode === 'dual' && mRatio) {
    return `최근 1주 vs 4주 ${sRatio} · 최근 4주 vs 12주 ${mRatio}${sp ? ' · ' + sp : ''}`;
  }
  if (d.mode === 'mid_only' && mRatio) {
    return `최근 4주 vs 12주 ${mRatio}${sp ? ' · ' + sp : ''}`;
  }
  /* short_only 또는 mode 미상 */
  return `최근 1주 vs 4주 ${sRatio}${sp ? ' · ' + sp : ''}`;
}

function formatVolatilitySub(d: ManipOk['dimensions']['volatility']): string {
  if (d.label === '데이터 부족') return '최근 20 영업일 데이터 수집 중';
  const cv = typeof d.cv === 'number' ? `${d.cv.toFixed(2)}%` : '?';
  const ar = typeof d.avgRange === 'number' ? `${d.avgRange.toFixed(2)}%` : '?';
  /* mode='relative' — 시장(KOSPI/KOSDAQ) 60일 cv 대비 상대비 표시
     mode='absolute' — market_index_daily 부재 시 절대 cv 폴백 (기존 표기 유지) */
  if (d.mode === 'relative' && typeof d.relCv === 'number' && d.relCv !== null) {
    const rel = `${d.relCv.toFixed(2)}배`;
    return `시장(KOSPI/KOSDAQ) 대비 ${rel} · 변동계수 ${cv} · 일중 변동폭 ${ar}`;
  }
  return `변동계수 ${cv} · 일중 변동폭 ${ar} (시장 데이터 없음)`;
}

function formatForeignSub(d: ManipOk['dimensions']['foreign']): string {
  /* 데이터 0건 — 종목 페이지 첫 진입 직후 KIS UPSERT 전 */
  if (d.current === null || d.current === undefined) {
    return '외국인 보유율 데이터 누적 중';
  }
  /* 1건만 있어 변동폭 미계산 — 현재값은 표시 */
  if (d.label === '관찰 시작') {
    return `현재 ${d.current.toFixed(2)}% · 변화 측정 시작 (다음 조회부터 추세 표시)`;
  }
  const cur = typeof d.current === 'number' ? `${d.current.toFixed(2)}%` : '?';
  const ch = typeof d.change === 'number' ? (d.change > 0 ? `+${d.change.toFixed(2)}` : d.change.toFixed(2)) : '?';
  return `현재 ${cur} · 1년 변화 ${ch}%p`;
}

function formatDisclosureSub(d: ManipOk['dimensions']['disclosure']): string {
  /* lookback 정책:
     - 유증/감자/합병: 1년
     - CB/BW: 2년 (만기 도래 시 주식 발행 영향)
     - 그 외: 6개월
     감쇠 가중치: 0~30일 100% / ~90일 70% / ~180일 50% / 그 외 30% */
  if (!d.count || d.count === 0) {
    return '최근 1~2년 자본거래 공시 없음 (안정)';
  }
  if (d.byType && Object.keys(d.byType).length > 0) {
    const TYPE_KO: Record<string, string> = {
      rights_issue: '유증',
      convertible_bond: 'CB',
      bond_with_warrant: 'BW',
      capital_reduction: '감자',
      share_cancellation: '자사주소각',
      stock_split: '액면분할',
      bonus_issue: '무증',
      stock_merge: '합병',
    };
    const parts = Object.entries(d.byType)
      .map(([k, v]) => `${TYPE_KO[k] ?? k} ${v}`)
      .join(', ');
    return `유증·감자 1년 / CB·BW 2년 누적 ${d.count}건 — ${parts}`;
  }
  return `유증·감자 1년 / CB·BW 2년 누적 ${d.count}건`;
}

/* ══════════════════════════════════════════════════════════════
 *  세력분석 AI 의견 — 보라 톤으로 종목분석 AI(파랑) 와 시각적 구분.
 *  2026-04-24 GET /tickers/:code/manipulation-ai 로 전환 (Phase 5) —
 *  기존 POST /manipulation-analysis 는 하위호환 위해 백엔드에 유지되나
 *  프론트는 4차원 메트릭 기반 신규 엔드포인트를 사용.
 * ══════════════════════════════════════════════════════════════ */

interface ManipAiResponse {
  /** insufficient_data 시 null */
  opinion: string | null;
  /** 'ok' 응답 시에만 존재 */
  analyzedAt?: string;
  reanalyzed?: boolean;
  stale?: boolean;
  /** insufficient_data 시 'insufficient_data' */
  reason?: string;
}

/* ══════════════════════════════════════════════════════════════
 *  세력분석 AI 의견 (claude -p CLI 버전) — AiManipSection 의 복사본.
 *  UI 동일, 호출 엔드포인트만 GET /tickers/:code/manipulation-ai-cli.
 *  queryKey 를 분리해 SDK 버전과 캐시가 섞이지 않도록 한다.
 * ══════════════════════════════════════════════════════════════ */

function AiManipCliSection({
  code,
  enabled,
}: {
  code?: string;
  /** 기존 호출 시그니처 호환용 — 서버가 basic 을 내부 조회하므로 미사용 */
  basic?: TickerBasicView;
  enabled: boolean;
}) {
  const isDomestic = !!code && /^\d{6}$/.test(code);
  const { data, isLoading, isError } = useQuery<ManipAiResponse>({
    queryKey: ['ticker', code, 'manip-ai-cli'],
    /* claude CLI 첫 호출은 SDK 보다 느릴 수 있어 90s 타임아웃 */
    queryFn: () =>
      get<ManipAiResponse>(`/tickers/${code}/manipulation-ai-cli`, undefined, { timeout: 90_000 }),
    enabled: enabled && isDomestic,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const payload = data;

  /* 국내 상장주 아닌 경우 (코인/해외주식 등) 섹션 전체 숨김 — 노이즈 방지 */
  if (!isDomestic) return null;

  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
      <h2 className="mb-3 section-title">AI 세력분석 의견</h2>

      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-purple-100 bg-purple-50 p-4 text-sm text-[var(--color-text-sub)]">
          <Spinner size="sm" />
          AI가 분석 중이에요. 분석하는데 다소 시간이 걸릴 수 있어요.
        </div>
      )}

      {!isLoading && isError && (
        <div className="rounded-lg border border-[var(--color-border)] bg-gray-50 p-4 text-sm text-[var(--color-text-sub)]">
          AI 분석을 불러올 수 없어요.
        </div>
      )}

      {/* insufficient_data — 신규 상장주·데이터 부족 */}
      {!isLoading && !isError && payload && payload.opinion === null && (
        <div className="rounded-lg border border-[var(--color-border)] bg-gray-50 p-4 text-sm text-[var(--color-text-sub)]">
          분석에 필요한 데이터가 부족해요. 일별 매매 데이터가 쌓이면 자동으로 생성돼요.
        </div>
      )}

      {/* ok — # 섹션 형식 마크다운을 AiAnalysisRenderer 가 카드로 렌더 */}
      {!isLoading && !isError && payload && payload.opinion && (
        <>
          <AiAnalysisRenderer analysis={payload.opinion} />
          {payload.stale && (
            <p className="mt-2 text-xs text-gray-400">(이전 분석 결과)</p>
          )}
        </>
      )}

      <p className="mt-3 text-xs text-[var(--color-text-sub)]">
        본 분석은 투자 권유가 아닌 참고용 정보이며, 투자 결과의 책임은 투자자 본인에게 있습니다.
      </p>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  TickerHeaderOpenClose — 헤더 카드 시초가 / 종가(전일) 라인
 *  StockQuoteCard 와 동일한 queryKey ['stocks','quote',code] 사용해
 *  React Query dedupe 로 API 호출은 한 번만 발생.
 * ════════════════════════════════════════════════════════════════ */
interface HeaderQuote {
  quote: { open: number | null; prevClose: number | null } | null;
}

function TickerHeaderOpenClose({ code }: { code: string }) {
  const { data } = useQuery({
    queryKey: ['stocks', 'quote', code],
    queryFn: () => get<HeaderQuote>(`/stocks/${code}/quote`),
    enabled: !!code && /^\d{6}$/.test(code),
    staleTime: 60_000,
  });
  const q = data?.quote;
  if (!q || (q.open == null && q.prevClose == null)) return null;
  const fmt = (v: number | null) =>
    v == null ? '-' : v.toLocaleString('ko-KR');
  return (
    <div className="mt-1 text-xs text-[var(--color-text-sub)]">
      {q.open != null && (
        <>
          시초가{' '}
          <span className="font-medium text-[var(--color-text)] tabular-nums">{fmt(q.open)}원</span>
        </>
      )}
      {q.open != null && q.prevClose != null && <span className="mx-2 opacity-50">|</span>}
      {q.prevClose != null && (
        <>
          종가{' '}
          <span className="font-medium text-[var(--color-text)] tabular-nums">{fmt(q.prevClose)}원</span>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  TickerTopTags — Phase 7
 *  종목 헤더 하단 태그 뱃지. 우선순위: risk → size → theme(confidence 내림차순) 최대 3개
 * ════════════════════════════════════════════════════════════════ */
interface TagsResponse {
  ticker: string;
  tags: Array<{
    slug: string;
    name: string;
    category: string;
    source: 'rule' | 'ai' | 'manual';
    confidence: number;
    note: string | null;
  }>;
  aiStatus: { status: string; lastAnalyzedAt: string | null };
}

/* 어드민 커스텀 뱃지 (ticker_badges) — 하단 태그 영역 끝에 지정 색상으로 표시 */
interface CustomBadge {
  id: number;
  label: string;
  color: string;
  text_color: string;
  sort_order: number;
}

function TickerTopTags({ code }: { code: string }) {
  const { data } = useQuery({
    queryKey: ['tickers', 'tags', code],
    queryFn: () => get<TagsResponse>(`/tickers/${code}/tags`),
    enabled: !!code && /^[0-9A-Z]{6}$/.test(code),
    staleTime: 5 * 60_000,
  });

  /* 어드민 커스텀 뱃지 — AI 태그 목록 끝에 함께 노출 (지정 색상 유지) */
  const { data: badgeData } = useQuery({
    queryKey: ['tickers', 'badges', code],
    queryFn: () => get<{ badges: CustomBadge[] }>(`/tickers/${code}/badges`),
    enabled: !!code,
    staleTime: 5 * 60_000,
  });
  const badges = badgeData?.badges ?? [];

  const tags = data?.tags ?? [];
  // AI 태그도 커스텀 뱃지도 없으면 영역 자체를 숨김
  if (tags.length === 0 && badges.length === 0) return null;

  /* 전체 태그 노출 — 카테고리 우선순위 정렬:
     risk → size → theme(confidence 내림차순) → style → sector → trait → valuechain */
  const order: Record<string, number> = {
    risk: 1, size: 2, theme: 3, style: 4, sector: 5, trait: 6, valuechain: 7,
  };
  const sorted = [...tags].sort((a, b) => {
    const ca = order[a.category] ?? 99;
    const cb = order[b.category] ?? 99;
    if (ca !== cb) return ca - cb;
    if (a.category === 'theme') return b.confidence - a.confidence;
    return 0;
  });

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[var(--color-border)] pt-3">
      {/* AI/분류 태그 — 기존 스타일 유지 */}
      {sorted.map((t) => (
        <TagBadge
          key={t.slug}
          slug={t.slug}
          name={t.name}
          category={t.category as TagCategory}
          source={t.source}
        />
      ))}
      {/* 어드민 커스텀 뱃지 — 목록 끝에 지정 색상으로 표시 */}
      {badges.map((b) => (
        <span
          key={`badge-${b.id}`}
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold"
          style={{ backgroundColor: b.color, color: b.text_color }}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
 *  차트분석 탭 (DEV) — 60일 캔들차트 + claude -p 패턴 분석
 * ══════════════════════════════════════════════════════════════ */

interface OhlcvCandleView {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}
interface OhlcvResp {
  candles: OhlcvCandleView[];
  dataDate: string | null;
}
interface ChartPatternView {
  name: string;
  description: string;
  reliability: number; // 1~5
  term?: string; // 단기 | 중기
}
interface ChartAnalysisView {
  stockCode: string;
  stockName: string | null;
  dataDate: string;
  analyzedAt: string;
  patterns: ChartPatternView[];
  summary: string;
  signal: string; // 강세 | 약세 | 중립
  reliability: number; // 0~100
}
type ChartAnalysisResp =
  | { status: 'done'; analysis: ChartAnalysisView }
  | { status: 'analyzing' }
  | { status: 'no_data' };

/** 장중 임시 캔들 — KIS basic info 에서 받은 오늘 OHLC */
interface TodayOHLC {
  date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  isMarketOpen: boolean;
}

/** 한국식 캔들차트(상승=빨강, 하락=파랑) — lightweight-charts.
 *  todayOHLC 가 있고 마지막 일봉 캔들의 date 와 다르면, 마지막에 "오늘 진행 중" 캔들을 추가.
 *  → 장중 가격이 실시간으로 차트에 반영. 장 마감 후 cron 으로 일봉 들어오면 자동으로 통합. */
function CandleChart({ candles, todayOHLC }: { candles: OhlcvCandleView[]; todayOHLC?: TodayOHLC | null }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || candles.length === 0) return;

    // 가격 표시: 소수점 제거 + 천단위 콤마 + "원" (Y축 라벨 / crosshair 공통)
    const priceFormatter = (price: number) => Math.round(price).toLocaleString('ko-KR') + '원';

    const chart: IChartApi = createChart(el, {
      width: el.clientWidth,
      height: 320,
      // attributionLogo: TradingView 로고 비표시(아래 텍스트 링크로 출처 표기 대체)
      layout: { textColor: '#6b7280', fontSize: 11, attributionLogo: false },
      grid: { vertLines: { color: '#f1f3f5' }, horzLines: { color: '#f1f3f5' } },
      rightPriceScale: { borderColor: '#e5e7eb' },
      /* timeScale.rightOffset — 우측에 5 bar 분량의 여백을 둬 마지막 캔들이 끝에 붙지 않고
         "현재(오늘) 시점" 이 우측에 자연스럽게 보이도록.
         fixRightEdge 는 해제 — true 면 rightOffset 적용 후에도 마지막 캔들을 강제로 끝에 붙임. */
      timeScale: { borderColor: '#e5e7eb', fixLeftEdge: true, fixRightEdge: false, rightOffset: 5 },
      // 날짜 표기: 년-월-일 순 (Y축 tick / crosshair 툴팁 공통)
      localization: { priceFormatter, dateFormat: 'yyyy-MM-dd' },
      handleScroll: false,
      handleScale: false,
    });
    const series = chart.addCandlestickSeries({
      upColor: '#e03131',
      downColor: '#1971c2',
      borderUpColor: '#e03131',
      borderDownColor: '#1971c2',
      wickUpColor: '#e03131',
      wickDownColor: '#1971c2',
      // Y축 가격 라벨도 동일 포맷(정수+콤마+원)
      priceFormat: { type: 'custom', formatter: priceFormatter, minMove: 1 },
    });
    /* 일봉 캔들 + (옵션) 오늘 진행 중 캔들.
       todayOHLC.date 가 마지막 일봉 date 와 다르고, 모든 OHLC 가 유효할 때만 추가. */
    const seriesData: CandlestickData[] = candles.map(
      (c): CandlestickData => ({
        time: c.date as Time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      }),
    );
    const lastDate = candles.length ? candles[candles.length - 1].date : null;
    if (
      todayOHLC &&
      todayOHLC.date !== lastDate &&
      todayOHLC.open != null &&
      todayOHLC.high != null &&
      todayOHLC.low != null &&
      todayOHLC.close != null
    ) {
      seriesData.push({
        time: todayOHLC.date as Time,
        open: todayOHLC.open,
        high: todayOHLC.high,
        low: todayOHLC.low,
        close: todayOHLC.close,
      });
    }
    series.setData(seriesData);
    /* 차트 영역을 "현재(오늘) 기준" 으로 표시:
       - 최근 90 영업일 + 오늘 임시 캔들까지 포함한 길이 기준으로 포커스
       - 우측에 rightOffset(5 bar) 만큼 여백 → 마지막 캔들 다음에 "현재 시점" 공간이 자연스럽게 노출
       - logical range 의 to 를 seriesData.length - 1 + rightOffset 으로 확장해 여백 포함 */
    {
      const rightOffset = 5;
      const totalLen = seriesData.length;
      const visibleCount = Math.min(90, totalLen);
      const from = Math.max(0, totalLen - visibleCount);
      const to = totalLen - 1 + rightOffset;
      if (to > from) {
        chart.timeScale().setVisibleLogicalRange({ from, to });
      }
    }

    const onResize = () => chart.applyOptions({ width: el.clientWidth });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chart.remove();
    };
  }, [candles, todayOHLC]);

  return <div ref={ref} className="w-full" />;
}

/** 1~5 별점 */
function StarRating({ value }: { value: number }) {
  const v = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span className="text-sm tabular-nums text-amber-500" aria-label={`신뢰도 ${v}/5`}>
      {'★'.repeat(v)}
      <span className="text-[var(--color-border)]">{'★'.repeat(5 - v)}</span>
    </span>
  );
}

/** 시그널 뱃지 — 강세(빨강)/약세(파랑)/중립(회색) */
function SignalBadge({ signal }: { signal: string }) {
  const cls =
    signal === '강세'
      ? 'bg-red-50 text-red-700'
      : signal === '약세'
      ? 'bg-blue-50 text-blue-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-sm font-bold', cls)}>
      {signal}
    </span>
  );
}

/** 패턴 설명 — 문장 단위 단락 분리 + 날짜/가격 나열 줄바꿈 + 3줄 더보기 */
function splitSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 날짜/가격이 여러 개 나열된 문장이면 ", " 기준으로 줄바꿈 */
function renderSentence(s: string) {
  const enumCount = (s.match(/\d+월|\d+일|[\d,]+원/g) ?? []).length;
  if (enumCount >= 3 && s.includes(', ')) {
    const parts = s.split(', ');
    return parts.map((p, i) => (
      <span key={i}>
        {i > 0 && <br />}
        {p}
      </span>
    ));
  }
  return s;
}

/** 긴 텍스트를 문장 단락 + 날짜/가격 나열 줄바꿈으로 표시(토글 없음) */
function FormattedText({ text, className }: { text: string; className?: string }) {
  const sentences = useMemo(() => splitSentences(text), [text]);
  return (
    <div className={cn('space-y-1.5', className)}>
      {sentences.map((s, i) => (
        <p key={i}>{renderSentence(s)}</p>
      ))}
    </div>
  );
}

/** 감지된 패턴 카드 — 도식 + 패턴명/방향/별점 + 설명.
 *  카드 전체 클릭 → 부모에서 모달 오픈(onOpen) — 사전 페이지로 이동하지 않음. */
function PatternCard({
  p,
  onOpen,
}: {
  p: ChartPatternView;
  onOpen: (args: { file: string; dir: '강세' | '약세' | '중립'; ai: ChartPatternView }) => void;
}) {
  const { file, dir } = classifyPattern(p.name);
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen({ file, dir, ai: p })}
        className="flex w-full gap-3 rounded-xl border border-[var(--color-border)] p-3 text-left transition-colors hover:border-[var(--color-primary)] hover:bg-[var(--color-bg)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
      >
        {/* 패턴 도식 SVG 이미지 (원본 다크 카드) */}
        <div className="shrink-0 self-start overflow-hidden rounded-lg border border-[var(--color-border)]">
          <PatternImg file={file} name={p.name} className="block h-[60px] w-[100px] object-cover" />
        </div>
        {/* 내용 */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-[var(--color-text)] underline decoration-dotted decoration-[var(--color-text-sub)] underline-offset-4">
              {p.name}
            </span>
            <DirBadge dir={dir} />
            <StarRating value={p.reliability} />
          </div>
          {p.description && (
            <FormattedText
              text={p.description}
              className="mt-1 text-xs leading-relaxed text-[var(--color-text-sub)]"
            />
          )}
        </div>
      </button>
    </li>
  );
}

function ChartAnalysisTab({ code, isActive }: { code?: string; isActive: boolean }) {
  const isDomestic = !!code && /^\d{6}$/.test(code);

  /* 감지된 패턴 카드 클릭 → 상세 모달 */
  const [patternModal, setPatternModal] = useState<{
    file: string;
    dir: '강세' | '약세' | '중립';
    ai: ChartPatternView;
  } | null>(null);

  const ohlcvQuery = useQuery<OhlcvResp>({
    queryKey: ['ticker', code, 'ohlcv120'],
    queryFn: () => get<OhlcvResp>(`/tickers/${code}/ohlcv?days=120`),
    enabled: isActive && isDomestic,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  /* 오늘 진행 중 OHLC — 장중 30초 폴링, 장 외에는 5분 staleTime + 폴링 X.
     서버는 KIS basic info (5분 캐시) 를 그대로 활용하므로 KIS 직접 호출 부담은 5분에 1회. */
  const todayQuery = useQuery<TodayOHLC | null>({
    queryKey: ['ticker', code, 'today'],
    queryFn: () => get<TodayOHLC | null>(`/tickers/${code}/today`),
    enabled: isActive && isDomestic,
    staleTime: 20_000,
    retry: false,
    refetchInterval: (q) => (q.state.data?.isMarketOpen ? 30_000 : false),
  });

  const analysisQuery = useQuery<ChartAnalysisResp>({
    queryKey: ['ticker', code, 'chart-analysis'],
    queryFn: () =>
      get<ChartAnalysisResp>(`/tickers/${code}/chart-analysis`, undefined, { timeout: 20_000 }),
    enabled: isActive && isDomestic,
    retry: false,
    /* 분석 중이면 5초 간격 폴링, 그 외엔 멈춤 */
    refetchInterval: (q) => (q.state.data?.status === 'analyzing' ? 5_000 : false),
  });

  if (!isDomestic) {
    return (
      <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <p className="text-sm text-[var(--color-text-sub)]">
          차트분석은 국내 상장(KOSPI/KOSDAQ) 종목만 지원해요.
        </p>
      </section>
    );
  }

  const candles = ohlcvQuery.data?.candles ?? [];
  const resp = analysisQuery.data;

  return (
    <div className="space-y-5">
      {/* 캔들차트 */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-white p-4 md:p-6">
        <h2 className="mb-3 section-title">일봉 차트 (120일)</h2>
        {ohlcvQuery.isLoading ? (
          <Skeleton className="h-[320px] w-full" />
        ) : candles.length > 0 ? (
          <>
            <CandleChart candles={candles} todayOHLC={todayQuery.data ?? null} />
            {/* 기준일 + (장중) LIVE 뱃지 — flex 로 세로 가운데 정렬해 LIVE 뱃지가 날짜와 동일선상에 위치 */}
            <div className="mt-2 flex items-center justify-end gap-1.5 text-[10px] text-[var(--color-text-sub)]">
              <span>
                기준일{' '}
                {todayQuery.data?.isMarketOpen && todayQuery.data?.date
                  ? todayQuery.data.date
                  : ohlcvQuery.data?.dataDate ?? '-'}
              </span>
              {todayQuery.data?.isMarketOpen && (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-0.5 text-[9px] font-bold text-red-600">
                  <span className="h-1 w-1 animate-pulse rounded-full bg-red-600" />
                  LIVE
                </span>
              )}
            </div>
            {/* TradingView 로고 비표시 → 출처 표기(최소화) */}
            <div className="text-right">
              <a
                href="https://www.tradingview.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[8px] text-[#bbb] opacity-50 transition-opacity hover:opacity-100"
              >
                Powered by TradingView
              </a>
            </div>
          </>
        ) : (
          <p className="py-10 text-center text-sm text-[var(--color-text-sub)]">
            차트 데이터가 없어요.
          </p>
        )}
      </section>

      {/* AI 패턴 분석 */}
      <section className="rounded-2xl border border-[var(--color-border)] bg-white p-6">
        <h2 className="mb-3 section-title">AI 차트 패턴 분석</h2>

        {/* 로딩(첫 조회) / 분석 중 */}
        {(analysisQuery.isLoading || resp?.status === 'analyzing') && (
          <div className="flex items-center gap-2 rounded-lg border border-purple-100 bg-purple-50 p-4 text-sm text-[var(--color-text-sub)]">
            <Spinner size="sm" />
            AI가 차트 패턴을 분석 중이에요. 최대 1~2분 정도 걸릴 수 있어요. (자동 갱신)
          </div>
        )}

        {/* 에러 → 재시도 */}
        {!analysisQuery.isLoading && analysisQuery.isError && (
          <div className="rounded-lg border border-[var(--color-border)] bg-gray-50 p-4 text-sm">
            <p className="text-[var(--color-text-sub)]">분석을 불러오지 못했어요.</p>
            <Button size="sm" variant="secondary" className="mt-3" onClick={() => analysisQuery.refetch()}>
              재시도
            </Button>
          </div>
        )}

        {/* 데이터 없음 */}
        {!analysisQuery.isLoading && !analysisQuery.isError && resp?.status === 'no_data' && (
          <p className="rounded-lg border border-[var(--color-border)] bg-gray-50 p-4 text-sm text-[var(--color-text-sub)]">
            아직 차트 데이터가 수집되지 않은 종목이에요.
          </p>
        )}

        {/* 완료 */}
        {resp?.status === 'done' && (
          <div className="space-y-5">
            {/* 시그널 + 종합 신뢰도 */}
            <div className="flex flex-wrap items-center gap-3">
              <SignalBadge signal={resp.analysis.signal} />
              <span className="text-sm text-[var(--color-text-sub)]">
                종합 신뢰도{' '}
                <span className="font-bold text-[var(--color-text)] tabular-nums">
                  {resp.analysis.reliability}
                </span>
                /100
              </span>
            </div>

            {/* 종합 총평 — 단기/중기 흐름, 길면 단락/줄바꿈 */}
            {resp.analysis.summary && (
              <FormattedText
                text={resp.analysis.summary}
                className="rounded-xl bg-[var(--color-bg)] px-4 py-3 text-sm leading-relaxed text-[var(--color-text)]"
              />
            )}

            {/* 감지된 패턴 목록 — 단기 / 중기 구분 */}
            <div>
              <h3 className="mb-2 text-sm font-bold text-[var(--color-text)]">감지된 패턴</h3>
              {resp.analysis.patterns.length === 0 ? (
                <p className="text-sm text-[var(--color-text-sub)]">뚜렷하게 감지된 패턴이 없어요.</p>
              ) : (
                <div className="space-y-4">
                  {(['단기', '중기'] as const).map((term) => {
                    const list = resp.analysis.patterns.filter((p) =>
                      term === '단기' ? p.term === '단기' : p.term !== '단기',
                    );
                    if (list.length === 0) return null;
                    return (
                      <div key={term}>
                        <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold text-[var(--color-text-sub)]">
                          <span
                            className={cn(
                              'inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold',
                              term === '단기'
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-slate-100 text-slate-600',
                            )}
                          >
                            {term}
                          </span>
                          {term === '단기' ? '최근 약 1개월 흐름' : '약 3~6개월 추세'}
                        </h4>
                        <ul className="space-y-2.5">
                          {list.map((p, i) => (
                            <PatternCard
                              key={`${term}-${p.name}-${i}`}
                              p={p}
                              onOpen={setPatternModal}
                            />
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <p className="text-right text-[10px] text-[var(--color-text-sub)]">
              분석 시각 · {new Date(resp.analysis.analyzedAt).toLocaleString('ko-KR')}
            </p>
          </div>
        )}

        {/* 패턴 사전 안내 CTA */}
        <div className="mt-4 flex flex-col items-center gap-2 rounded-xl bg-[var(--color-bg)] px-4 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-sm font-semibold text-[var(--color-text)]">
            패턴에 대해 더 알고 싶으신가요?
          </p>
          <Link
            to="/chart-patterns"
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-primary)] px-4 py-2 text-sm font-bold text-white transition-opacity hover:opacity-90"
          >
            차트 패턴 사전 보기 →
          </Link>
        </div>

        <p className="mt-3 text-xs text-[var(--color-text-sub)]">
          본 분석은 투자 권유가 아닌 참고용 정보이며, 투자 결과의 책임은 투자자 본인에게 있습니다.
        </p>
      </section>

      {/* 패턴 상세 모달 — 감지된 패턴 카드 클릭 시 */}
      <PatternDetailModal
        open={!!patternModal}
        onClose={() => setPatternModal(null)}
        file={patternModal?.file ?? ''}
        dir={patternModal?.dir ?? '중립'}
        ai={{
          name: patternModal?.ai.name ?? '',
          description: patternModal?.ai.description,
        }}
      />
    </div>
  );
}
